<template>
  <v-menu
    v-model="menuVisible"
    offset-y
    @keydown.esc="menuVisible = false"
  >
    <template v-slot:activator="{ on }">
      <div>
        <v-btn
          rounded
          text
          outlined
          class="text-none"
          v-on="on"
        >
          <v-icon
            x-small
            class="mr-2"
          >
            far fa-pen
          </v-icon>
          {{ $t('button.edit_columns') }}
        </v-btn>
      </div>
    </template>

    <v-card width="250">
      <v-text-field
        v-model="search"
        dense
        outlined
        hide-details
        :label="$t('text_input.search.placeholder')"
        class="pa-2"
        clearable
        @click.stop
      />
      <div style="overflow: auto; flex-grow: 1; height: 300px">
        <draggable
          :list="selectedVariables"
          handle=".handle"
          :group="{ name: 'variables' }"
          :invert-swap="true"
          :swap-treshold="0.6"
          :animation="100"
          @update="updateOrder"
          @change="updateOrder"
        >
          <div
            v-for="(variable, index) in selectedVariables"
            :key="'selected' + variable.id + index"
          >
            <div>
              <v-list-item
                @click.stop="unselectVariable(variable)"
              >
                <v-list-item-action>
                  <v-checkbox
                    v-model="variable.selected"
                    @click.stop="unselectVariable(variable)"
                  />
                </v-list-item-action>
                <v-list-item-content class="pa-0">
                  <v-list-item-title>{{ variable.text }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon
                    small
                    class="handle"
                    style="cursor: move"
                  >
                    far fa-grip-lines
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
            </div>
          </div>
        </draggable>
        <v-divider v-if="filteredUnselectedVariables.length" />
        <v-list>
          <v-list-item
            v-for="variable in filteredUnselectedVariables"
            :key="variable.id"
            @click.stop="selectVariable(variable)"
          >
            <v-list-item-action>
              <v-checkbox
                :value="variable.selected"
                @click.stop="selectVariable(variable)"
              />
            </v-list-item-action>
            <v-list-item-content class="pa-0">
              <v-list-item-title>{{ variable.text }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import draggable from 'vuedraggable'
import debounce from 'lodash/debounce'
import { CoachApiService } from '@/data/services'
import { orderBy } from 'lodash'

export default {
  name: 'VariableSelector',
  components: { draggable },
  props: {
    variables: {
      type: Object,
      required: true
    },
    category: {
      type: String,
      required: true
    },
    dashboard: {
      type: String,
      default: null
    }
  },
  data: () => ({
    selectedVariables: [],
    unselectedVariables: [],
    menuVisible: false,
    search: ''
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    filteredUnselectedVariables() {
      if (this.search === null) return this.unselectedVariables

      return this.unselectedVariables.filter(v => v.text.toLowerCase().includes(this.search.toLowerCase()))
    }
  },
  watch: {
    variables: {
      deep: true,
      handler: function (val) {
        this.selectedVariables = orderBy(val.player[this.category].filter(v => v.selected), ['order'])
        this.unselectedVariables = orderBy(val.player[this.category].filter(v => !v.selected), ['order'])
      }
    },
    category: function (val) {
      this.selectedVariables = orderBy(this.variables.player[val].filter(v => v.selected), ['order'])
      this.unselectedVariables = orderBy(this.variables.player[val].filter(v => !v.selected), ['order'])
    }
  },
  mounted() {
    this.selectedVariables = orderBy(this.variables.player[this.category].filter(v => v.selected), ['order'])
    this.unselectedVariables = orderBy(this.variables.player[this.category].filter(v => !v.selected), ['order'])
  },
  methods: {
    selectVariable(variable) {
      this.unselectedVariables = this.unselectedVariables.filter(v => v.id !== variable.id)
      variable.selected = true
      variable.order = this.selectedVariables.length + 1
      this.selectedVariables.push(variable)

      if (this.dashboard) this.save()
    },
    unselectVariable(variable) {
      this.selectedVariables = this.selectedVariables.filter(v => v.id !== variable.id)
      variable.selected = false
      variable.order = null
      this.unselectedVariables.push(variable)

      if (this.dashboard) this.save()
    },
    updateOrder(event) {
      this.selectedVariables.forEach((variable, index) => {
        variable.order = index + 1
      })

      if (this.dashboard) this.save()
    },
    save: debounce(async function () {
      this.submitting = true

      try {
        const data = {
          team: Object.entries(this.variables.team)
            .filter(([key]) => key !== 'SUMMARY')
            .flatMap(([, values]) => values)
            .filter(variable => variable.selected)
            .map(variable => ({
              id: variable.id,
              order: variable.order
            })).filter(v => v.order),

          player: Object.entries(this.variables.player)
            .filter(([key]) => key !== 'SUMMARY')
            .flatMap(([, values]) => values)
            .filter(variable => variable.selected)
            .map(variable => ({
              id: variable.id,
              order: variable.order
            })).filter(v => v.order),

          summary_team: this.variables.team.SUMMARY.filter(variable => variable.selected).map(variable => ({
            id: variable.id,
            order: variable.order
          })).filter(v => v.order),

          summary_player: this.variables.player.SUMMARY.filter(variable => variable.selected).map(variable => ({
            id: variable.id,
            order: variable.order
          })).filter(v => v.order)
        }

        await CoachApiService.saveVariables(this.user.coach.id, {
          ...data,
          dashboard: this.dashboard
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.variables_save.error'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    }, 1000)
  }
}
</script>

<style scoped>

</style>
